import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ApiPost } from "../../helpers/API/ApiData";
import * as authUtil from "../../utils/auth.util";
import * as userUtil from "../../utils/user.util";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FirstLoginPasswordChange from "./FirstLoginPage";
import { EMAIL_REGEX } from "../../utils/validation";

export default function Login() {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPasssword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminInfoForPasswordChange, setAdminInfoForPasswordChange] = useState<any>(null);
  const handleChange = (e: any) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };
  const handleSubmit = async (e: any) => {
    setLoading(true);
    e.preventDefault();
    if (!loginData.emailId && !loginData.password) {
      setLoading(false);
      setErrors({
        emailId: "E-Mail ist erforderlich*",
        password: "Passwort wird benötigt*",
      });
    } else if (loginData.emailId === "" && loginData.password === "") {
      setErrors({ ...errors, emailId: "E-Mail ist erforderlich*" });
      setLoading(false);
    } else if (!loginData.emailId || loginData.emailId === "") {
      setErrors({ ...errors, emailId: "E-Mail ist erforderlich*" });
      setLoading(false);
    } else if (
      !loginData.emailId ||
      EMAIL_REGEX.test(loginData.emailId) === false
    ) {
      setErrors({ ...errors, emailId: "E-Mail ist ungültig*" });
      setLoading(false);
    } else if (!loginData.password || loginData.password === "") {
      setErrors({ ...errors, password: "Passwort wird benötigt*" });
      setLoading(false);
    } else {
      loginData.emailId = loginData.emailId.toLowerCase();
      await ApiPost("admin/login", loginData)
        .then((res: any) => {
          setLoading(false);
          const token = res?.data?.data?.token;
          const adminInfo = res?.data?.data?.adminInfo;

          // Check if admin needs to change password on first login
          if (adminInfo?.requirePasswordChange) {
            // Store token temporarily so the password-change API call can authenticate.
            // Token is stored only after password change completes via FirstLoginPage.
            authUtil.setAdminToken(token);
            setAdminInfoForPasswordChange(adminInfo);
            setShowPasswordModal(true);
            toast.info("Bitte ändern Sie Ihr temporäres Passwort", {
              autoClose: 5000,
            });
          } else {
            // Only persist auth when no password change is required
            authUtil.setAdminToken(token);
            userUtil.setAdminInfo(adminInfo);
            toast.success("Anmeldung erfolgreich", {
              autoClose: 5000,
            });
            adminInfo?.role === "chatbot"
              ? (window.location.href = "/message-center")
              : (window.location.href = "/dashboard");
          }
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Benutzer:in existiert nicht");
          setLoading(false);
        });
    }
  };
  const handleKeyPress = (e: any) => {
    e.preventDefault();
    handleSubmit(e);
  };
  const showHidePassword = (e: any) => {
    setShowPasssword(!showPassword);
  };

  const handlePasswordModalClose = () => {
    // Remove temp token since password change was not completed
    localStorage.removeItem("Admintoken");
    setShowPasswordModal(false);
    setAdminInfoForPasswordChange(null);
  };

  return (
    <>
      {showPasswordModal && adminInfoForPasswordChange && (
        <FirstLoginPasswordChange
          open={showPasswordModal}
          onClose={handlePasswordModalClose}
          adminInfo={adminInfoForPasswordChange}
        />
      )}
      <Box sx={{ maxWidth: 450, width: "100%", mx: "auto" }}>
        <Box sx={{ textAlign: "center", mb: { xs: 5, lg: 10 } }}>
          <Typography variant="h4" component="h3">
            Anmelden
          </Typography>
          <Typography color="text.secondary" fontWeight="bold">
            Geben Sie Ihre Anmeldeinformationen ein.
          </Typography>
          {errors.user && (
            <Typography color="error" variant="subtitle1">
              {errors.user}
            </Typography>
          )}
        </Box>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
        />
        <form onSubmit={(e) => handleKeyPress(e)}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="E-Mail"
              type="email"
              variant="outlined"
              margin="normal"
              name="emailId"
              onChange={(e) => {
                handleChange(e);
              }}
              error={!!errors.emailId}
              helperText={errors.emailId}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Passwort"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              margin="normal"
              name="password"
              onChange={(e) => {
                handleChange(e);
              }}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={(e) => showHidePassword(e)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              color="brand"
              size="large"
              type="submit"
              disabled={loading}
              sx={{ my: 1.5, py: 1.5 }}
            >
              Einloggen
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{ ml: 2, color: "white" }}
                />
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </>
  );
}
